import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import './TextViewer.scss';
import {highlightElement, scrollToElementFromHighlight} from './ViewerUtils';
import {InDocumentSearchProps} from './PdfViewer.types';
import {Spinner} from '../spinner/spinner';

interface TextViewerProps {
    title: string;
    content: string;
    isLoading: boolean;
    onInDocumentSearchPropsChange: (inDocumentSearchProps: InDocumentSearchProps) => void;
}

export const TextViewer = memo(({title, content, isLoading, onInDocumentSearchPropsChange}: TextViewerProps) => {
    const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
    const [annotationIds, setAnnotationIds] = useState<string[]>([]);
    const [contentWithEmTags, setContentWithEmTags] = useState<string>('');
    const [titleWithEmTags, setTitleWithEmTags] = useState<string>('');
    const refPreviewPageScroll: React.RefObject<any> = useRef(null);

    const onHighlightIndexChange = useCallback(
        (highlightIndex: number) => {
            setHighlightedIndex(highlightIndex);
            const htmlElement = document.getElementById(annotationIds[highlightIndex]);
            if (htmlElement) {
                scrollToElementFromHighlight(htmlElement, refPreviewPageScroll);
                highlightElement(htmlElement);
            }
        },
        [annotationIds],
    );

    useEffect(() => {
        setAnnotationIds(() => []);
        setContentWithEmTags(() => addIdsToEmTags(content, 'content'));
        setTitleWithEmTags(() => addIdsToEmTags(title, 'title'));
    }, [content, title]);

    useEffect(() => {
        const data: InDocumentSearchProps = {
            numHighlights: annotationIds.length,
            highlightedIndex: highlightedIndex,
            onHighlightIndexChange: onHighlightIndexChange,
            onSearchTriggered: onSearchTriggered,
        };
        onInDocumentSearchPropsChange(data);
    }, [annotationIds.length, highlightedIndex, onHighlightIndexChange, onInDocumentSearchPropsChange]);

    const addIdsToEmTags = (textWithEmTags: string, idClass: string) => {
        if (!textWithEmTags) {
            return '';
        }
        let index = 0;
        return textWithEmTags.replace(/(<em>)/g, () => {
            const highlightsId = 'hibu_' + idClass + '_hl_' + index++;
            setAnnotationIds((prev) => [...prev, highlightsId]);
            return '<em id="' + highlightsId + '">';
        });
    };

    useEffect(() => {
        onHighlightIndexChange(0);
        //eslint-disable-next-line
    }, [title, content]);

    const onSearchTriggered = () => {
        setHighlightedIndex(0);
    };

    return (
        <div id={'hibuTextPreview'}>
            {isLoading ? (
                <Spinner/>
            ) : (
                <div id={'hibuTextPreviewPageWrapper'}>
                    <div id={'hibuTextPreviewPageScroll'} ref={refPreviewPageScroll}>
                        <div id={'hibuTextPreviewPage'}>
                            <h1 id={'textViewerTitle'} dangerouslySetInnerHTML={{__html: titleWithEmTags}}/>
                            <pre id={'textViewerContent'} dangerouslySetInnerHTML={{__html: contentWithEmTags}}/>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});
